import { useState } from "react";
import { notification } from "antd";
import { useDataProvider, useInvalidate } from "@refinedev/core";
import { Button } from "@libertify/ui";
import { useSession } from "hooks/useSession";
import type { BundleResponse } from "@/types";
import { exportsResource } from "../../hooks/useDataExports";
import { isExpired } from "../../helpers/dataExport";
import { useTranslate } from "#src/hooks";

type DownloadActionProps = {
  bundle: BundleResponse;
};

export const DownloadAction = ({ bundle }: DownloadActionProps) => {
  const t = useTranslate();
  const { currentOrganization } = useSession();
  const dataProvider = useDataProvider();
  const invalidate = useInvalidate();
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    const orgId = currentOrganization?.id;
    if (!orgId) return;
    const resource = exportsResource(orgId);

    setLoading(true);
    try {
      // download_url is a ~1h signed S3 URL the backend regenerates on every
      // GET, so we must re-fetch right before opening it. useDataProvider
      // gives us an awaitable one-shot fetch through the same auth/error
      // pipeline as the rest of the app — useOne would be a permanent
      // subscription per row, which we don't want.
      const { data: fresh } = await dataProvider().getOne<BundleResponse>({
        resource,
        id: bundle.id,
      });
      await invalidate({
        resource,
        id: bundle.id,
        invalidates: ["detail", "list"],
      });

      if (
        fresh.status !== "Completed" ||
        isExpired(fresh) ||
        !fresh.download_url
      ) {
        return;
      }
      window.open(fresh.download_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error downloading data export", error);
      notification.error({
        message: t("media.organizations.dataExport.failed"),
        placement: "bottomLeft",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="primary"
      loading={loading}
      onClick={() => {
        handleDownload().catch((error) => {
          console.error("Error downloading data export", error);
        });
      }}
    >
      {t("media.organizations.dataExport.download")}
    </Button>
  );
};

export default DownloadAction;
